import '../Pages.scss';
import React, { FunctionComponent } from 'react';

import FeatureImage from '../../components/FeatureImage/FeatureImage';
import InMemoryHelmet from '../../components/Helmet/InMemoryHelmet';
import Page from '../../components/Page/Page';
import { Banner } from '../../components/Banner/Banner';
import { InMemoryTotal } from '../../components/InMemoryTotal/InMemoryTotal';
import { InMemoryCTA } from '../../components/InMemoryCTA/InMemoryCTA';
import { InMemoryFundraisersList } from '../../components/InMemoryFundraisersList/InMemoryFundraisersList';
import { InMemorySupportersList } from '../../components/InMemorySupportersList/InMemorySupportersList';
import { InMemoryCharitiesList } from '../../components/InMemoryCharitiesList/InMemoryCharitiesList';
import styles from './InMemoryPage.scss';
import { MODAL } from '../../context';
import { ShareModal } from '../../components/ShareModal/ShareModal';
import { buildQuery, useApp, usePageQuery } from '../../hooks';
import { useTranslation } from 'react-i18next';
import { InMemoryShareFragment } from '../../../types/__generated__';
import { gql } from '@apollo/client';
import { InMemoryShareProvider } from '../../components/SocialButtons/InMemoryShareProvider';
import { CheckoutModal } from '../../components/CheckoutModal/CheckoutModal';
import { PopupBlockedModal } from '../../components/PopupBlockedModal/PopupBlockedModal';
import { PopupRefocusModal } from '../../components/PopupRefocusModal/PopupRefocusModal';
import { NativeShareModal } from '../../components/NativeShareModal/NativeShareModal';
import { ShareModalPlatforms } from '../../components/NativeShareModal/NativeShareEnums';

const InMemoryPage: FunctionComponent = () => {
  const { hideModal, openModal } = useApp();
  const { heading, description } = useShareModal();

  return (
    <InMemoryShareProvider>
      <Page>
        {openModal === MODAL.SHARE && (
          <ShareModal
            heading={heading}
            description={description}
            onClose={hideModal}
          />
        )}
        {openModal === MODAL.CHECKOUT && <CheckoutModal onClose={hideModal} />}
        {openModal === MODAL.POPUP_BLOCKED && (
          <PopupBlockedModal onClose={hideModal} />
        )}
        {openModal === MODAL.INSTAGRAM_SHARE && (
          <NativeShareModal
            onClose={hideModal}
            platform={ShareModalPlatforms.INSTAGRAM}
          />
        )}
        {openModal === MODAL.POPUP_REFOCUS && (
          <PopupRefocusModal onClose={hideModal} />
        )}
        <InMemoryHelmet />
        <Banner />
        <div data-qa="app" className={styles.container}>
          <main className={styles.grid}>
            <div className={styles.featureImage}>
              <FeatureImage enableLightbox={true} />
            </div>
            <section className={styles.summaryWrapper}>
              <div className={styles.summary}>
                <InMemoryTotal />
              </div>
              <div className={styles.callsToAction}>
                <InMemoryCTA />
              </div>
            </section>
            <section className={styles.fundraisers}>
              <InMemoryFundraisersList />
            </section>
            <section className={styles.donations}>
              <InMemorySupportersList />
            </section>
            <section className={styles.beneficiaries}>
              <InMemoryCharitiesList />
            </section>
          </main>
        </div>
      </Page>
    </InMemoryShareProvider>
  );
};

export default InMemoryPage;

const FRAGMENT = buildQuery(gql`
  fragment InMemoryShare on Page {
    inMemory {
      firstNameOfDeceased
    }
  }
`);

function useShareModal() {
  const { t } = useTranslation('common', { useSuspense: false });
  const { data } = usePageQuery<InMemoryShareFragment>(FRAGMENT);

  const { firstNameOfDeceased } = data?.inMemory || {};

  const heading = firstNameOfDeceased
    ? t('inMemory.cta.modalHeading', {
        firstNameOfDeceased,
      })
    : t('inMemory.cta.modalHeadingFallback');

  const description = t('shareModal.description');

  return {
    heading,
    description,
  };
}
